import Strapi from 'strapi-sdk-js';
import Cookies from 'js-cookie';
import { DB_URL } from '../util/DB_URL';
import { socket } from '../sockets';
import {
  announceFreePlanBlocked,
  announceModuleNotAvailable,
} from '../util/entitlements';
import { readRolloutNotEnabled } from '../util/rollout';

/**
 * ☠️ Server-side calls (getStaticProps, getServerSideProps, API routes) give
 * up after SERVER_TIMEOUT_MS. axios's default is to wait forever, and on
 * 2026-10-29 calls to the API that never came back held every marketing page
 * render on a Vercel instance until the 50s function timeout, for ~30 minutes.
 * The browser keeps the default: some dashboard reports legitimately run long.
 */
export const SERVER_TIMEOUT_MS = 10000;
const serverTimeout = typeof window === 'undefined' ? { timeout: SERVER_TIMEOUT_MS } : {};

/**
 * Configured Strapi SDK instance
 * Centralized API client for all query modules
 */
export const strapi = new Strapi({
  url: DB_URL,
  prefix: '/api',
  store: {
    key: 'jwt',
    useLocalStorage: false,
    cookieOptions: { path: '/' },
  },
  axiosOptions: {
    headers: {},
    ...serverTimeout,
  },
});

/**
 * Safety-net request interceptor: ensures the `company` header is present on
 * every outgoing request.  withAuth.js sets it on the axios defaults after the
 * full auth flow completes, but there is a brief window on page refresh where
 * the header may not yet be restored.  This interceptor reads the company
 * cookie as a fallback so requests that fire during that window still carry the
 * correct multi-tenant identifier.
 */
/**
 * Global 403 handler.
 *
 * A MODULE deny — the server's coded 403 `{ error: { name: 'ModuleNotAvailable',
 * details: { reason: 'module_not_available', module, key? } } }`
 * (FEATURE_MODULARIZATION_ADDONS_PLAN.md §9.4) — keeps the server's message and
 * details on the error, and announces itself with a window
 * `CustomEvent('tc:module-not-available', { detail: { module, key } })`, which
 * `components/upsell/ModuleUpgradeModal` (mounted once in layout-1) turns into
 * the upgrade dialog. It is an upsell, not a permission problem.
 *
 * A FREE-preset refusal — any status whose `error.details` has a `free_*`
 * `reason` and an `upgradeUrl` (company `refuseModuleWrite`, the product /
 * option / inquiry caps, the Connect and selling-mode locks) — keeps its
 * message and fires `CustomEvent('tc:free-plan-blocked', { detail: { reason,
 * upgradeUrl } })`, which the same mounted modal turns into `UpgradeModal`.
 *
 * A ROLLOUT deny — the Configurable Product Builder's coded 403 `{ error: {
 * name: 'RolloutNotEnabled', details: { reason: 'rollout_not_enabled', flag } } }`
 * (docs/configurable-product-builder ADR-007) — is handled like a module deny
 * (the server's message and details stay on the error) but fires NO event and
 * opens no dialog: an unreleased feature is not something to upsell.
 *
 * Every OTHER 403 is a permission deny: its message is rewritten so any
 * per-mutation onError handler that reads `error.message` displays the
 * permission message instead of a generic fallback like "Failed to save
 * changes".
 *
 * The Strapi SDK catches axios errors and re-throws `e.response.data`,
 * so modifying `response.data` here flows through to React Query's onError.
 */
strapi.axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response && announceFreePlanBlocked(error)) {
      return Promise.reject(error);
    }
    if (error?.response?.status === 403) {
      const moduleDeny = announceModuleNotAvailable(error) || readRolloutNotEnabled(error);
      if (moduleDeny) {
        const serverMessage = error.response.data?.error?.message;
        if (error.response.data && serverMessage && !error.response.data.message) {
          error.response.data.message = serverMessage;
        }
        return Promise.reject(error);
      }

      const permissionMsg = 'You do not have permission to perform this action.';
      if (error.response.data) {
        error.response.data.message = permissionMsg;
        if (error.response.data.error) {
          error.response.data.error.message = permissionMsg;
        }
      }
    }
    return Promise.reject(error);
  }
);

strapi.axios.interceptors.request.use((config) => {
  if (!config.headers['company'] && typeof window !== 'undefined') {
    try {
      const companyCookie = Cookies.get('company');
      if (companyCookie) {
        const parsed = JSON.parse(companyCookie);
        if (parsed?.id) {
          config.headers['company'] = parsed.id;
        }
      }
    } catch {
      // Cookie missing or malformed — skip silently
    }
  }

  // Attach socket ID so the backend can exclude this user from socket broadcasts.
  // Prevents duplicate refetches when the user who saved also receives the socket event.
  if (socket?.connected && socket.id) {
    config.headers['x-socket-id'] = socket.id;
  }

  return config;
});

/**
 * Creates an isolated Strapi instance for SSR with request-specific headers
 * This prevents race conditions when multiple requests are processed concurrently
 *
 * @param {Object} options - Configuration options
 * @param {string} options.jwt - JWT token for authentication
 * @param {number} options.companyId - Company ID for the request
 * @returns {Strapi} Isolated Strapi instance
 */
export function createSSRStrapiInstance({ jwt, companyId }) {
  const headers = {};

  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }

  if (companyId && !isNaN(companyId)) {
    headers['company'] = companyId;
  }

  return new Strapi({
    url: DB_URL,
    prefix: '/api',
    store: {
      key: 'jwt',
      useLocalStorage: false,
      cookieOptions: { path: '/' },
    },
    axiosOptions: {
      headers,
      ...serverTimeout,
    },
  });
}

export default strapi;